import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { CATEGORY_LIST } from "../../services/CategoryModel";
import styles from "./EditProfileModal.module.css";

/* Edit profile modal sheet with interactive interest tags & bio editing */
export function EditProfileModal({
  isOpen,
  onClose,
  onSave,
  userData,
  setUserData,
}) {
  // Local draft copy of userData: edits happen here first and are only
  // committed (via handleSaveClick) when the user hits Save, so closing
  // without saving discards any in-progress changes.
  const [formData, setFormData] = useState(userData || {});

  // Re-sync the draft whenever the modal (re)opens or userData changes
  // upstream, so it always starts from the current saved profile rather
  // than stale data left over from a previous edit session.
  useEffect(() => {
    if (userData) {
      setFormData(userData);
    }
  }, [userData, isOpen]);

  if (!isOpen) return null;

  // Add/remove a single interest tag from the draft's interests array.
  const handleToggleInterest = (catId) => {
    const currentInterests = formData.interests || [];
    if (currentInterests.includes(catId)) {
      setFormData({
        ...formData,
        interests: currentInterests.filter((i) => i !== catId),
      });
    } else {
      setFormData({
        ...formData,
        interests: [...currentInterests, catId],
      });
    }
  };

  const handleSaveClick = () => {
    if (setUserData) {
      setUserData(formData);
    }
    if (onSave) {
      onSave(formData);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.sectionSpaceLg}>
        <div className={styles.headerRow}>
          <h3 className={styles.title}>Edit Profile & Interests</h3>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
        </div>

        <div className={styles.sectionSpaceSm}>
          <div>
            <label className={styles.fieldLabel}>Full Name</label>
            <input
              type="text"
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.fieldLabel}>Gender</label>
            <select
              value={formData.gender || ""}
              onChange={(e) =>
                setFormData({ ...formData, gender: e.target.value })
              }
              className={styles.textInput}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </div>

          {/* Campo de Biografía añadido en edición */}
          <div>
            <label className={styles.fieldLabel}>Bio / About You</label>
            <textarea
              value={formData.bio || ""}
              rows={3}
              placeholder="Tell others a bit about yourself..."
              onChange={(e) =>
                setFormData({ ...formData, bio: e.target.value })
              }
              className={styles.textArea}
            />
          </div>

          <div>
            <label className={styles.fieldLabelBlock}>Interests</label>
            <div className={styles.interestsRow}>
              {CATEGORY_LIST.map((cat) => {
                const isSelected = formData.interests?.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleToggleInterest(cat.id)}
                    className={
                      isSelected
                        ? styles.interestPillActive
                        : styles.interestPillInactive
                    }
                  >
                    {isSelected && <Check size={12} />}
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSaveClick}
        className={styles.saveButton}
      >
        Save Changes
      </button>
    </div>
  );
}
